import { useMemo, type ReactNode } from 'react'
import { LayerCard } from '@cloudflare/kumo'
import {
  businessHoursSlots,
  formatHourMinute,
  wallClockToUtc,
  weekNeedsExtendedHours,
} from '../lib/calendar-time'
import { formatChipIdentity } from '../lib/calendar-chip'
import { roomColorByIndex } from '../lib/room-colors'
import { useTranslation } from '../i18n'
import type {
  BuildingCalendarResponse,
  RoomCalendarDto,
  RoomCalendarEventDto,
} from '@hub-wifi/shared'

/** Weekday short-name keys (Mon..Sun) for i18n lookup. */
const WEEKDAY_SHORT_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

/**
 * Tagged event used internally — flattens the {rooms[].events[]} payload into
 * a single list so the grid renderer can group/iterate per slot regardless of
 * the originating room.
 */
interface TaggedEvent {
  event: RoomCalendarEventDto
  room: RoomCalendarDto
  roomIndex: number
}

export interface BuildingCalendarTimeGridViewProps {
  /** Ordered list of YYYY-MM-DD (1 for day view, 7 for week) in the building tz. */
  days: string[]
  /** Calendar payload already filtered to the visible range by the parent. */
  data: BuildingCalendarResponse
  /** i18n string for the grid's aria-label. */
  ariaLabel: string
  /** data-testid for the outer grid element (e.g. 'calendar-day-grid'). */
  gridTestId: string
  /**
   * Optional weekday-header renderer. Default: weekday-short + dd (matches the
   * previous week-view header). Day view can override to show a full date.
   */
  renderDayHeader?: (dayYmd: string, isToday: boolean, index: number) => ReactNode
}

/**
 * Shared time-grid renderer used by both Day and Week views of the building
 * calendar. Renders a 30-minute grid with N day-columns and an adaptive hour
 * range (08:00–20:00 by default, expanded to 00:00–24:00 if any event in the
 * visible range falls outside business hours).
 *
 * Events render as colored chips stacked vertically in each slot they
 * overlap. Each chip is two lines: identity (top, bold — coworker + company
 * for hub bookings, organizer / company for outlook events) and the room
 * name (bottom). The hover tooltip (native `title` attr — avoids a
 * TooltipProvider for what could be hundreds of chips on a busy week)
 * repeats the identity line, plus room and time range. "External" is the
 * fallback label when the organizer email's domain doesn't match any
 * active Company. Color derives from the room's position in `data.rooms`
 * via `roomColorByIndex`.
 */
export function BuildingCalendarTimeGridView(props: BuildingCalendarTimeGridViewProps) {
  const { days, data, ariaLabel, gridTestId, renderDayHeader } = props
  const timezone = data.buildingTimezone
  const { t } = useTranslation()

  // Flatten rooms[].events[] -> tagged event list. The roomIndex is the
  // room's position in `data.rooms`, which is the palette indexing key.
  const taggedEvents = useMemo<TaggedEvent[]>(
    () =>
      data.rooms.flatMap((room, roomIndex) =>
        room.events.map((event) => ({ event, room, roomIndex })),
      ),
    [data.rooms],
  )

  // Adaptive hours: expand the whole visible range to 24h if any single event
  // falls outside 08:00–20:00. weekNeedsExtendedHours is timezone-aware and
  // now serves both day & week views (name preserved for now).
  const flatEvents = useMemo(() => taggedEvents.map((te) => te.event), [taggedEvents])
  const extended = useMemo(
    () => weekNeedsExtendedHours(flatEvents, timezone),
    [flatEvents, timezone],
  )
  const slots = useMemo(
    () => (extended ? businessHoursSlots(0, 24) : businessHoursSlots()),
    [extended],
  )

  const todayYmd = useMemo(
    () =>
      new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date()),
    [timezone],
  )

  /**
   * Current hour in the building tz as a two-digit prefix (e.g., "14"). A
   * cell is highlighted with the accent background when it sits in today's
   * column AND its slot label starts with this prefix — i.e., only the two
   * 30-min slots covering the current clock hour. Recomputed once per
   * render; visual staleness across the hour boundary is acceptable for
   * the admin's session.
   */
  const currentHourPrefix = useMemo(() => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      hour12: false,
    }).formatToParts(new Date())
    const hour = parts.find((p) => p.type === 'hour')?.value ?? '00'
    // Intl can emit "24" at midnight in some locales — normalize.
    return hour === '24' ? '00' : hour.padStart(2, '0')
  }, [timezone])

  /**
   * Index tagged events by `${dayYmd}|${slot}` for O(1) lookup during render.
   * For each 30-minute cell, push every event whose UTC range overlaps the
   * cell's UTC range. Uses `wallClockToUtc` to convert the cell's wall-clock
   * boundaries into UTC instants comparable with `event.startAt`/`endAt`.
   */
  const cellMap = useMemo(() => {
    const m = new Map<string, TaggedEvent[]>()
    for (const tagged of taggedEvents) {
      const evStart = new Date(tagged.event.startAt)
      const evEnd = new Date(tagged.event.endAt)
      if (Number.isNaN(evStart.getTime()) || Number.isNaN(evEnd.getTime())) continue
      for (const dayYmd of days) {
        for (const slot of slots) {
          const slotStart = wallClockToUtc(dayYmd, slot, timezone)
          const slotEnd = new Date(slotStart.getTime() + 30 * 60 * 1000)
          if (evStart < slotEnd && evEnd > slotStart) {
            const key = `${dayYmd}|${slot}`
            const arr = m.get(key) ?? []
            arr.push(tagged)
            m.set(key, arr)
          }
        }
      }
    }
    return m
  }, [days, slots, taggedEvents, timezone])

  const defaultRenderDayHeader = (dayYmd: string, _isToday: boolean, _index: number): ReactNode => {
    // Derive the weekday from the date itself (not column index) so the
    // single-day grid shows the correct weekday for the selected date, and
    // the 7-day grid stays correct regardless of whether the parent passes
    // a Mon-anchored array.
    const parts = dayYmd.split('-').map(Number)
    let key: (typeof WEEKDAY_SHORT_KEYS)[number] = 'mon'
    if (parts.length === 3 && !parts.some(Number.isNaN)) {
      const [y, m, d] = parts as [number, number, number]
      const jsDay = new Date(Date.UTC(y, m - 1, d, 12, 0, 0)).getUTCDay() // 0=Sun..6=Sat
      const mondayIdx = (jsDay + 6) % 7 // 0=Mon..6=Sun
      key = WEEKDAY_SHORT_KEYS[mondayIdx] ?? 'mon'
    }
    return (
      <>
        <div>{t(`buildingCalendar.weekdayShort.${key}`)}</div>
        <div style={{ fontSize: '11px', opacity: 0.75 }}>{dayYmd.slice(8, 10)}</div>
      </>
    )
  }

  const headerRenderer = renderDayHeader ?? defaultRenderDayHeader

  return (
    <LayerCard>
      <LayerCard.Primary>
        <div
          role="grid"
          aria-label={ariaLabel}
          data-testid={gridTestId}
          style={{
            display: 'grid',
            gridTemplateColumns: `64px repeat(${days.length}, 1fr)`,
            gap: '1px',
            background: 'var(--color-kumo-line)',
            border: '1px solid var(--color-kumo-line)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          {/* Header row: empty corner + N day headers */}
          <div style={{ background: 'var(--color-kumo-base)' }} />
          {days.map((dayYmd, i) => {
            const isToday = dayYmd === todayYmd
            return (
              <div
                key={dayYmd}
                role="columnheader"
                style={{
                  background: 'var(--color-kumo-base)',
                  padding: '8px',
                  textAlign: 'center',
                  fontWeight: isToday ? 700 : 500,
                  color: isToday ? 'var(--text-color-kumo-link)' : undefined,
                  fontSize: '12px',
                }}
              >
                {headerRenderer(dayYmd, isToday, i)}
              </div>
            )
          })}

          {/* Slot rows: row header (HH:MM) + N cells */}
          {slots.map((slot) => (
            <SlotRow
              key={slot}
              slot={slot}
              days={days}
              todayYmd={todayYmd}
              currentHourPrefix={currentHourPrefix}
              cellMap={cellMap}
              timezone={timezone}
              externalLabel={t('buildingCalendar.external')}
            />
          ))}
        </div>
      </LayerCard.Primary>
    </LayerCard>
  )
}

/**
 * Single row of the time grid: one time-axis label + N day cells. Extracted
 * so that per-row work isn't recomputed for every render of every cell.
 */
function SlotRow(props: {
  slot: string
  days: string[]
  todayYmd: string
  /** Two-digit hour prefix (e.g. "14") for the current clock hour in tz. */
  currentHourPrefix: string
  cellMap: Map<string, ReadonlyArray<TaggedEvent>>
  timezone: string
  externalLabel: string
}) {
  const { slot, days, todayYmd, currentHourPrefix, cellMap, timezone, externalLabel } = props
  // Highlight applies only to cells that are BOTH today's column AND inside
  // the current clock hour (slot label starts with the current hour prefix,
  // e.g. "14:" matches "14:00" and "14:30"). Avoids painting the entire
  // today column accent — focuses attention on "now".
  const slotIsCurrentHour = slot.startsWith(`${currentHourPrefix}:`)
  return (
    <>
      <div
        role="rowheader"
        style={{
          background: 'var(--color-kumo-base)',
          padding: '4px 6px',
          fontSize: '11px',
          textAlign: 'right',
          color: 'var(--text-color-kumo-subtle)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {slot}
      </div>
      {days.map((dayYmd) => {
        const cell = cellMap.get(`${dayYmd}|${slot}`) ?? []
        const isToday = dayYmd === todayYmd
        const highlight = isToday && slotIsCurrentHour
        return (
          <div
            key={`${dayYmd}|${slot}`}
            role="gridcell"
            style={{
              background: highlight ? 'var(--color-kumo-info-tint)' : 'var(--color-kumo-base)',
              minHeight: '36px',
              padding: '2px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              alignContent: 'flex-start',
            }}
          >
            {cell.map((tagged) => {
              const { event, room, roomIndex } = tagged
              const color = roomColorByIndex(roomIndex)
              const range = `${formatHourMinute(event.startAt, timezone)}–${formatHourMinute(event.endAt, timezone)}`
              const identityLabel = formatChipIdentity(event, externalLabel)
              const tooltipParts = [identityLabel, room.roomName, range]
              if (event.source === 'hub' && event.coworkerEmail) {
                tooltipParts.push(event.coworkerEmail)
              }
              const title = tooltipParts.join('\n')
              return (
                <span
                  key={`${event.id}-${dayYmd}-${slot}`}
                  data-testid={`calendar-chip-${event.id}`}
                  title={title}
                  style={{
                    background: color,
                    color: 'white',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    lineHeight: '13px',
                    cursor: 'default',
                    userSelect: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {identityLabel}
                  </span>
                  <span
                    style={{
                      fontWeight: 400,
                      opacity: 0.9,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {room.roomName}
                  </span>
                </span>
              )
            })}
          </div>
        )
      })}
    </>
  )
}
